/**
 * Cycle derivation. See docs/12-derivation-and-prediction.md Section 1.
 *
 * Pure. No I/O, no clock except the `today` parameter. This is the code most
 * likely to be subtly wrong (R-03), so it is also the code that must be
 * testable without a browser, a database or a render.
 */
import { addDays, compareDates, daysBetween, isAfter } from './date'
import type { Cycle, DayEntry, DerivedCycles, ISODate, Period } from './types'
import { BLEEDING_LEVELS } from './types'

/**
 * Below this, two logged events are almost certainly one period recorded with
 * a gap rather than two cycles.
 */
export const MIN_CYCLE = 15

/**
 * Above this the user very likely stopped logging. Including such a span would
 * badly skew the median even though the median is otherwise robust.
 */
export const MAX_CYCLE = 90

/** A skipped log day mid-period is common. User-configurable. */
export const DEFAULT_MAX_GAP_DAYS = 1

export interface CycleOptions {
  maxGapDays?: number
  /** Entries dated after this are stored (FR-214) but excluded from derivation. */
  today?: ISODate
}

const isBleedingDay = (entry: DayEntry): boolean =>
  entry.flow !== undefined && BLEEDING_LEVELS.includes(entry.flow)

/**
 * Group bleeding days into periods and periods into cycles.
 *
 * Deliberately takes the full entry set rather than a pre-filtered one: the
 * filtering rules (spotting is not bleeding; future entries do not count) are
 * part of the derivation and belong here where they are tested, not at each
 * call site where they would drift.
 */
export function deriveCycles(
  entries: readonly DayEntry[],
  options: CycleOptions = {},
): DerivedCycles {
  const maxGapDays = options.maxGapDays ?? DEFAULT_MAX_GAP_DAYS
  const horizon = options.today

  const bleedingDates = entries
    .filter(isBleedingDay)
    .map((e) => e.date)
    .filter((d) => horizon === undefined || !isAfter(d, horizon))
    .sort(compareDates)

  const periods = groupIntoPeriods(bleedingDates, maxGapDays)
  const cycles = buildCycles(periods)

  const result: DerivedCycles = { periods, cycles }

  const last = periods[periods.length - 1]
  if (last && horizon !== undefined) {
    // The period is "current" while today is still inside it, or within the
    // gap tolerance of its end — the user may simply not have logged yet.
    if (daysBetween(last.end, horizon) <= maxGapDays) {
      result.currentPeriod = last
    }
    result.currentCycle = {
      start: last.start,
      dayOfCycle: daysBetween(last.start, horizon) + 1,
    }
  }

  return result
}

function groupIntoPeriods(sortedDates: ISODate[], maxGapDays: number): Period[] {
  const periods: Period[] = []
  let start: ISODate | undefined
  let previous: ISODate | undefined

  for (const date of sortedDates) {
    if (start === undefined || previous === undefined) {
      start = date
      previous = date
      continue
    }
    // A gap of `maxGapDays` missing days means daysBetween is maxGapDays + 1.
    if (daysBetween(previous, date) > maxGapDays + 1) {
      periods.push(makePeriod(start, previous))
      start = date
    }
    previous = date
  }

  if (start !== undefined && previous !== undefined) {
    periods.push(makePeriod(start, previous))
  }
  return periods
}

const makePeriod = (start: ISODate, end: ISODate): Period => ({
  start,
  end,
  length: daysBetween(start, end) + 1,
})

function buildCycles(periods: Period[]): Cycle[] {
  const cycles: Cycle[] = []
  for (let i = 0; i < periods.length - 1; i++) {
    const current = periods[i]!
    const next = periods[i + 1]!
    const length = daysBetween(current.start, next.start)
    cycles.push({
      start: current.start,
      end: addDays(next.start, -1),
      length,
      periodLength: current.length,
      valid: length >= MIN_CYCLE && length <= MAX_CYCLE,
    })
  }
  return cycles
}

/** Completed cycles whose length is inside the plausible range. */
export const validCycles = (cycles: readonly Cycle[]): Cycle[] => cycles.filter((c) => c.valid)
